/**
 * Export a single offer to PDF (client-side) using jsPDF.
 * Classic = original layout (accent bar, two-column header, meta box, terms box, table with images).
 * Minimal = clean typography, no colored boxes, text-only table.
 * Compact = invoice-style one-line header, dense layout.
 * When templateDefinition is provided (full designer), layout is driven by that definition.
 * See docs/offer-pdf-designer-plan.md for full designer plan.
 */
import type { Offer, OfferLineItem } from '../modules/offers/useData';
import { fetchGreekFontBase64, registerGreekFont, GREEK_FONT_ID } from './greekPdfFont';
import { API_BASE_URL } from '../config/apiConfig';
import api from '../services/api';
import type { OfferPdfTemplateDefinition, OfferPdfSectionType } from './offerPdfTypes';

export type { OfferPdfTemplateDefinition } from './offerPdfTypes';

const PAGE_WIDTH = 210;

/** Ensure there is enough vertical space; add a new page if not. Returns the (possibly reset) y. */
function ensureSpace(doc: import('jspdf').jsPDF, y: number, needed: number, margin: number): number {
  const pageH = doc.internal.pageSize.getHeight();
  if (y + needed > pageH - margin) {
    doc.addPage();
    return margin;
  }
  return y;
}

/** Template identifier for the light version (fixed styles). */
export type OfferPdfTemplateId = 'classic' | 'minimal' | 'compact';

/** Human-readable labels for template selector. */
export const OFFER_PDF_TEMPLATE_OPTIONS: { value: OfferPdfTemplateId; label: string }[] = [
  { value: 'classic', label: 'Classic' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'compact', label: 'Compact' },
];

const LABELS_EN = {
  title: 'ECONOMIC OFFER',
  to: 'To:',
  signatory: 'Attn:',
  date: 'Date:',
  offerNo: 'Offer No:',
  validUntil: 'Valid Until:',
  validity: 'Validity:',
  delivery: 'Delivery:',
  payment: 'Payment:',
  name: 'Name',
  description: 'Description',
  model: 'Model',
  qty: 'Qty',
  unitPrice: 'Unit price',
  discount: 'Discount',
  total: 'Total',
  grossAmount: 'Gross Amount:',
  totalDiscount: 'Discount:',
  netAmount: 'Net Amount:',
  vatRate: 'VAT:',
  grandTotal: 'Grand Total:',
  vatNote: '(All amounts exclude VAT unless stated)',
};

const LABELS_EL: typeof LABELS_EN = {
  title: 'ΟΙΚΟΝΟΜΙΚΗ ΠΡΟΣΦΟΡΑ',
  to: 'Προς:',
  signatory: 'Υπόψη:',
  date: 'Ημ/νία:',
  offerNo: 'Αρ. Προσφοράς:',
  validUntil: 'Ισχύς Έως:',
  validity: 'Η προσφορά ισχύει:',
  delivery: 'Ημερομηνία Παράδοσης:',
  payment: 'Τρόπος Πληρωμής:',
  name: 'Όνομα',
  description: 'Περιγραφή',
  model: 'Μοντέλο',
  qty: 'Ποσ',
  unitPrice: 'Κόστος',
  discount: 'Έκπτωση',
  total: 'Σύνολο',
  grossAmount: 'Μεικτό Ποσό:',
  totalDiscount: 'Έκπτωση:',
  netAmount: 'Καθαρό Ποσό:',
  vatRate: 'ΦΠΑ:',
  grandTotal: 'Γενικό Σύνολο:',
  vatNote: '(Όλες οι αναγραφόμενες τιμές δεν περιλαμβάνουν ΦΠΑ)',
};

/** Optional overrides when a section has exact position (mm). Used by section drawers. */
export interface SectionLayoutOverrides {
  marginLeft?: number;
  contentWidth?: number;
  heightMm?: number;
}

function formatDate(d: string | null | undefined): string {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return String(d);
  }
}

function formatCurrency(amount: number | null | undefined): string {
  if (amount == null) return '';
  return new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}

/** Load image from URL; returns an HTMLImageElement for jsPDF or null on failure. */
function loadLogoImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/** Result for jsPDF addImage: use data URL so blob URLs are never passed (avoids ERR_FILE_NOT_FOUND after revoke). */
export interface LoadedImageData {
  dataUrl: string;
  format: 'PNG' | 'JPEG';
  width: number;
  height: number;
}

/** Load image via API client (sends Bearer token) and return base64 data URL + dimensions for jsPDF, or null. */
function loadImageViaApi(url: string): Promise<LoadedImageData | null> {
  const path = url.startsWith(API_BASE_URL) ? url.slice(API_BASE_URL.length) : url;
  if (!path || !path.startsWith('/')) return Promise.resolve(null);
  return api
    .get(path, { responseType: 'blob' })
    .then((res) => {
      const blob = res.data as Blob;
      const format = blob.type && blob.type.includes('jpeg') ? 'JPEG' : 'PNG';
      return new Promise<LoadedImageData | null>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result as string;
          if (!dataUrl || typeof dataUrl !== 'string') {
            resolve(null);
            return;
          }
          const img = new Image();
          img.onload = () => {
            resolve({ dataUrl, format, width: img.naturalWidth || img.width, height: img.naturalHeight || img.height });
          };
          img.onerror = () => resolve(null);
          img.src = dataUrl;
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    })
    .catch(() => null);
}

const LOGO_MAX_WIDTH_MM = 45;
const LOGO_MAX_HEIGHT_MM = 18;
const ROW_IMAGE_SIZE_MM = 12;

function getCatalogItemImageUrl(lineItem: OfferLineItem): string | null {
  const cat = lineItem.catalogItem;
  if (!cat?.FileAttachment?.length) return null;
  const primaryId = cat.primaryFileAttachmentId;
  const chosen = primaryId
    ? cat.FileAttachment.find((f) => f.id === primaryId)
    : cat.FileAttachment[0];
  const file = chosen || cat.FileAttachment[0];
  if (!file?.id || !cat.id) return null;
  const type = (file.fileType || '').toLowerCase();
  if (!type.startsWith('image/')) return null;
  return `${API_BASE_URL}/catalog-items/${cat.id}/files/${file.id}`;
}

export interface OfferPdfExportOptions {
  /** PDF style template (light version: classic, minimal, compact). Default: classic. Ignored when templateDefinition is set. */
  templateId?: OfferPdfTemplateId;
  /** Full designer: use this definition to drive layout. When set, templateId is ignored. */
  templateDefinition?: OfferPdfTemplateDefinition | null;
  /** Logo URL (e.g. from company profile). */
  logoUrlOverride?: string;
  /** When true, logo URL is auth-required (e.g. /settings/company-profile/logo); load with credentials. */
  logoRequiresAuth?: boolean;
  /** Use these company details as fallback when the offer has no company fields (e.g. from company profile). */
  companyDetails?: {
    companyName?: string | null;
    companyAddress?: string | null;
    companyPhone?: string | null;
    companyEmail?: string | null;
    signatoryName?: string | null;
  };
  /** When true, return the PDF as a Blob instead of triggering download (e.g. for preview). */
  returnBlob?: boolean;
}

/** Shared context for all templates. */
interface PdfRenderContext {
  doc: import('jspdf').jsPDF;
  font: string;
  LABELS: typeof LABELS_EN;
  useGreek: boolean;
  logoData: LoadedImageData | { img: HTMLImageElement; format: 'PNG' | 'JPEG'; width: number; height: number } | null;
  companyName: string | null;
  companyAddress: string | null;
  companyPhone: string | null;
  companyEmail: string | null;
  signatoryName: string | null;
  contentWidth: number;
  clientName: string;
}

/** Table data precomputed for definition-driven render. */
interface DefinitionTableData {
  rowImageData: (LoadedImageData | null)[];
  rowsClassic: string[][];
  rowsTextOnly: string[][];
  tableHeadersClassic: string[];
  tableHeadersMinimal: string[];
  lineItems: OfferLineItem[];
}

/** Draw product cell with bold name and normal description. Used in didDrawCell callbacks. */
function drawProductCell(
  doc: import('jspdf').jsPDF,
  cell: { x: number; y: number; width: number; height: number },
  lineItem: OfferLineItem,
  font: string,
  fontSize: number,
  textColor: readonly number[],
) {
  const pad = 3;
  const x = cell.x + pad;
  let cy = cell.y + pad + fontSize * 0.35;
  const maxW = cell.width - pad * 2;

  const bg = (cell as any).styles?.fillColor;
  if (bg && Array.isArray(bg)) doc.setFillColor(bg[0], bg[1], bg[2]);
  else doc.setFillColor(255, 255, 255);
  doc.rect(cell.x + 0.5, cell.y + 0.5, cell.width - 1, cell.height - 1, 'F');

  if (lineItem.name) {
    doc.setFont(font, 'bold');
    doc.setFontSize(fontSize);
    doc.setTextColor(textColor[0], textColor[1], textColor[2]);
    const nameLines: string[] = doc.splitTextToSize(lineItem.name, maxW);
    nameLines.forEach((line: string) => {
      doc.text(line, x, cy);
      cy += fontSize * 0.45;
    });
  }

  if (lineItem.description) {
    cy += 0.5;
    doc.setFont(font, 'normal');
    doc.setFontSize(fontSize - 1);
    doc.setTextColor(100, 116, 139);
    const descLines: string[] = doc.splitTextToSize(lineItem.description, maxW);
    descLines.forEach((line: string) => {
      doc.text(line, x, cy);
      cy += (fontSize - 1) * 0.45;
    });
  }
}

/** Get page width in mm from format. */
function getPageWidth(format: 'a4' | 'letter', orientation: 'portrait' | 'landscape'): number {
  const a4W = 210;
  const a4H = 297;
  const letterW = 215.9;
  const letterH = 279.4;
  if (format === 'a4') return orientation === 'portrait' ? a4W : a4H;
  return orientation === 'portrait' ? letterW : letterH;
}

/** Draw accent bar section from definition. */
async function drawAccentBar(
  doc: import('jspdf').jsPDF,
  y: number,
  _offer: Offer,
  _ctx: PdfRenderContext,
  def: OfferPdfTemplateDefinition,
  overrides?: SectionLayoutOverrides
): Promise<number> {
  const t = def.tokens;
  const height = overrides?.heightMm ?? t.accentBarHeightMm ?? 5;
  if (height <= 0) return y;
  const color = t.accentColor ?? [41, 98, 255];
  const pageW = getPageWidth(def.page.format, def.page.orientation);
  doc.setFillColor(...color);
  doc.rect(0, y, pageW, height, 'F');
  return y + height + 10;
}

/** Draw letterhead (logo + company) from definition. */
async function drawLetterhead(
  doc: import('jspdf').jsPDF,
  y: number,
  _offer: Offer,
  ctx: PdfRenderContext,
  def: OfferPdfTemplateDefinition,
  overrides?: SectionLayoutOverrides
): Promise<number> {
  const pageW = getPageWidth(def.page.format, def.page.orientation);
  const margin = overrides?.marginLeft ?? def.page.marginMm;
  const contentWidth = overrides?.contentWidth ?? pageW - 2 * def.page.marginMm;
  const colLeftWidth = contentWidth * 0.52;
  const t = def.tokens;
  const bodySize = t.bodyFontSize ?? 9;
  const { font, logoData, companyName, companyAddress, companyPhone, companyEmail, signatoryName } = ctx;

  let curY = y;
  if (logoData && logoData.width > 0 && logoData.height > 0) {
    const scale = Math.min(LOGO_MAX_WIDTH_MM / (logoData.width / 3.78), LOGO_MAX_HEIGHT_MM / (logoData.height / 3.78), 1);
    const drawW = (logoData.width / 3.78) * scale;
    const drawH = (logoData.height / 3.78) * scale;
    const imgSrc = 'dataUrl' in logoData ? logoData.dataUrl : logoData.img;
    if (imgSrc) {
      doc.addImage(imgSrc, logoData.format, margin, curY, drawW, drawH);
      curY += drawH + 5;
    }
  }

  doc.setTextColor(51, 65, 85);
  if (companyName) {
    doc.setFont(font, 'bold');
    doc.setFontSize(bodySize + 1);
    doc.text(companyName, margin, curY);
    curY += 6;
  }
  if (companyAddress) {
    doc.setFont(font, 'normal');
    doc.setFontSize(bodySize);
    const addrLines = doc.splitTextToSize(companyAddress, colLeftWidth);
    addrLines.forEach((line: string) => {
      doc.text(line, margin, curY);
      curY += 4.5;
    });
  }
  if (companyPhone || companyEmail) {
    doc.setFontSize(bodySize);
    if (companyPhone) {
      doc.text(`Tel: ${companyPhone}`, margin, curY);
      curY += 4.5;
    }
    if (companyEmail) {
      doc.text(`Email: ${companyEmail}`, margin, curY);
      curY += 4.5;
    }
  }
  if (signatoryName) {
    doc.setFont(font, 'bold');
    doc.setFontSize(bodySize);
    doc.text(signatoryName, margin, curY);
    curY += 5;
  }
  return curY;
}

/** Draw title + meta box (right column) from definition. */
async function drawTitleMeta(
  doc: import('jspdf').jsPDF,
  startY: number,
  offer: Offer,
  ctx: PdfRenderContext,
  def: OfferPdfTemplateDefinition,
  overrides?: SectionLayoutOverrides
): Promise<number> {
  const pageW = getPageWidth(def.page.format, def.page.orientation);
  const margin = overrides?.marginLeft ?? def.page.marginMm;
  const contentWidth = overrides?.contentWidth ?? pageW - 2 * def.page.marginMm;
  const colLeftWidth = contentWidth * 0.52;
  const colRightStart = margin + colLeftWidth + 8;
  const metaBoxWidth = contentWidth - colLeftWidth - 8;
  const t = def.tokens;
  const titleSize = t.titleFontSize ?? 20;
  const bodySize = t.bodyFontSize ?? 9;
  const metaColor = t.metaBoxColor ?? [245, 247, 250];
  const borderColor = t.borderColor ?? [220, 223, 228];
  const { font, LABELS } = ctx;

  doc.setFont(font, 'bold');
  doc.setFontSize(titleSize);
  doc.setTextColor(...(t.primaryTextColor ?? [30, 41, 59]));
  doc.text(LABELS.title, colRightStart + 4, startY + 5);

  const metaBoxY = startY + 10;
  const metaLines: string[] = [];
  metaLines.push(`${LABELS.offerNo} ${offer.offerNumber}`);
  metaLines.push(`${LABELS.to} ${ctx.clientName}`);
  if (offer.signatoryName) metaLines.push(`${LABELS.signatory} ${offer.signatoryName}`);
  metaLines.push(`${LABELS.date} ${formatDate(offer.offerDate)}`);
  if (offer.validUntil) metaLines.push(`${LABELS.validUntil} ${formatDate(offer.validUntil)}`);
  const metaBoxHeight = Math.max(32, metaLines.length * 6 + 8);
  doc.setDrawColor(...borderColor);
  doc.setLineWidth(0.2);
  doc.setFillColor(...metaColor);
  doc.roundedRect(colRightStart, metaBoxY, metaBoxWidth, metaBoxHeight, 1.5, 1.5, 'FD');

  doc.setFont(font, 'normal');
  doc.setFontSize(bodySize);
  doc.setTextColor(51, 65, 85);
  metaLines.forEach((line, i) => {
    doc.text(line, colRightStart + 4, metaBoxY + 8 + i * 6);
  });

  return metaBoxY + metaBoxHeight;
}

/** Draw intro text from definition. */
async function drawIntro(
  doc: import('jspdf').jsPDF,
  y: number,
  offer: Offer,
  ctx: PdfRenderContext,
  def: OfferPdfTemplateDefinition,
  overrides?: SectionLayoutOverrides
): Promise<number> {
  if (!offer.introText) return y;
  const pageW = getPageWidth(def.page.format, def.page.orientation);
  const margin = overrides?.marginLeft ?? def.page.marginMm;
  const contentWidth = overrides?.contentWidth ?? pageW - 2 * def.page.marginMm;
  const t = def.tokens;
  const bodySize = t.bodyFontSize ?? 9;
  const color = t.secondaryTextColor ?? [71, 85, 105];
  doc.setFont(ctx.font, 'normal');
  doc.setFontSize(bodySize);
  doc.setTextColor(...color);
  const introLines = doc.splitTextToSize(offer.introText, contentWidth);
  introLines.forEach((line: string) => {
    doc.text(line, margin, y);
    y += 5;
  });
  return y + 6;
}

/** Draw line items table from definition. */
async function drawLineItemsTable(
  doc: import('jspdf').jsPDF,
  y: number,
  offer: Offer,
  ctx: PdfRenderContext,
  def: OfferPdfTemplateDefinition,
  tableData: DefinitionTableData,
  overrides?: SectionLayoutOverrides
): Promise<number> {
  const pageW = getPageWidth(def.page.format, def.page.orientation);
  const margin = overrides?.marginLeft ?? def.page.marginMm;
  const contentWidth = overrides?.contentWidth ?? pageW - 2 * def.page.marginMm;
  const t = def.tokens;
  const tableConfig = def.table ?? {};
  const showImages = tableConfig.showProductImages !== false;
  const headerColor = t.tableHeaderColor ?? [52, 73, 94];
  const bodySize = t.bodyFontSize ?? 9;
  const alternateFill = tableConfig.alternateRowFill ?? [250, 250, 252];

  const rows = showImages ? tableData.rowsClassic : tableData.rowsTextOnly;
  const headers = showImages ? tableData.tableHeadersClassic : tableData.tableHeadersMinimal;
  if (rows.length === 0) return y;

  const autoTable = (await import('jspdf-autotable')).default;
  const hasDiscount = headers.length > (showImages ? 5 : 4);
  const columnStyles = showImages
    ? hasDiscount
      ? {
          0: { cellWidth: ROW_IMAGE_SIZE_MM + 4, minCellHeight: ROW_IMAGE_SIZE_MM + 4 },
          1: { cellWidth: 'auto' as const },
          2: { halign: 'right' as const, valign: 'middle' as const, cellWidth: 14 },
          3: { halign: 'right' as const, valign: 'middle' as const, cellWidth: 24 },
          4: { halign: 'right' as const, valign: 'middle' as const, cellWidth: 22 },
          5: { halign: 'right' as const, valign: 'middle' as const, cellWidth: 24 },
        }
      : {
          0: { cellWidth: ROW_IMAGE_SIZE_MM + 4, minCellHeight: ROW_IMAGE_SIZE_MM + 4 },
          1: { cellWidth: 'auto' as const },
          2: { halign: 'right' as const, valign: 'middle' as const, cellWidth: 16 },
          3: { halign: 'right' as const, valign: 'middle' as const, cellWidth: 28 },
          4: { halign: 'right' as const, valign: 'middle' as const, cellWidth: 28 },
        }
    : hasDiscount
      ? {
          0: { cellWidth: 'auto' as const },
          1: { halign: 'right' as const, valign: 'middle' as const, cellWidth: 14 },
          2: { halign: 'right' as const, valign: 'middle' as const, cellWidth: 24 },
          3: { halign: 'right' as const, valign: 'middle' as const, cellWidth: 22 },
          4: { halign: 'right' as const, valign: 'middle' as const, cellWidth: 24 },
        }
      : {
          0: { cellWidth: 'auto' as const },
          1: { halign: 'right' as const, valign: 'middle' as const, cellWidth: 14 },
          2: { halign: 'right' as const, valign: 'middle' as const, cellWidth: 24 },
          3: { halign: 'right' as const, valign: 'middle' as const, cellWidth: 24 },
        };

  const productColIdx = showImages ? 1 : 0;
  const didDrawCell = (data: { section: string; column: { index: number }; row: { index: number }; cell: { x: number; y: number; width: number; height: number } }) => {
    if (data.section !== 'body') return;
    if (showImages && data.column.index === 0) {
      const dataItem = tableData.rowImageData[data.row.index];
      if (dataItem?.dataUrl && dataItem.width > 0 && dataItem.height > 0) {
        const cell = data.cell;
        const pad = 1.5;
        const w = cell.width - pad * 2;
        const h = cell.height - pad * 2;
        const scale = Math.min(w / (dataItem.width / 3.78), h / (dataItem.height / 3.78), 1);
        const drawW = (dataItem.width / 3.78) * scale;
        const drawH = (dataItem.height / 3.78) * scale;
        const cx = cell.x + cell.width / 2;
        const cy = cell.y + cell.height / 2;
        doc.addImage(dataItem.dataUrl, dataItem.format, cx - drawW / 2, cy - drawH / 2, drawW, drawH);
      }
    }
    if (data.column.index === productColIdx) {
      const li = tableData.lineItems[data.row.index];
      if (li) drawProductCell(doc, data.cell, li, ctx.font, bodySize, [51, 65, 85]);
    }
  };

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: y,
    styles: { fontSize: bodySize, cellPadding: 3, font: ctx.font, textColor: [51, 65, 85] },
    headStyles: {
      fillColor: headerColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      font: ctx.font,
      fontSize: bodySize,
      cellPadding: 4,
    },
    alternateRowStyles: { fillColor: alternateFill },
    margin: { top: margin, bottom: margin, left: margin, right: pageW - margin - contentWidth },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columnStyles: columnStyles as any,
    theme: 'plain',
    rowPageBreak: 'avoid',
    didDrawCell,
  });
  return (doc as any).lastAutoTable.finalY + 8;
}

/** Draw terms box from definition. */
async function drawTerms(
  doc: import('jspdf').jsPDF,
  y: number,
  offer: Offer,
  ctx: PdfRenderContext,
  def: OfferPdfTemplateDefinition,
  overrides?: SectionLayoutOverrides
): Promise<number> {
  const hasTerms = !!(offer.validityText || offer.deliveryText || offer.paymentMethodText);
  if (!hasTerms) return y;
  const pageW = getPageWidth(def.page.format, def.page.orientation);
  const margin = overrides?.marginLeft ?? def.page.marginMm;
  const contentWidth = overrides?.contentWidth ?? pageW - 2 * def.page.marginMm;
  const t = def.tokens;
  const termsColor = t.termsBoxColor ?? [248, 250, 252];
  const borderColor = t.borderColor ?? [220, 223, 228];
  const termLines: string[] = [];
  if (offer.validityText) termLines.push(`${ctx.LABELS.validity} ${offer.validityText}`);
  if (offer.deliveryText) termLines.push(`${ctx.LABELS.delivery} ${offer.deliveryText}`);
  if (offer.paymentMethodText) termLines.push(`${ctx.LABELS.payment} ${offer.paymentMethodText}`);
  const termBoxPadding = 5;
  const termBoxHeight = termLines.length * 5 + termBoxPadding * 2;
  doc.setFillColor(...termsColor);
  doc.setDrawColor(...borderColor);
  doc.roundedRect(margin, y - 2, contentWidth, termBoxHeight, 1.5, 1.5, 'FD');
  doc.setFont(ctx.font, 'normal');
  doc.setFontSize(t.bodyFontSize ?? 9);
  termLines.forEach((line: string, i: number) => {
    doc.text(line, margin + termBoxPadding, y + 5 + i * 5);
  });
  return y + termBoxHeight + 6;
}

/** Draw notes from definition. */
async function drawNotes(
  doc: import('jspdf').jsPDF,
  y: number,
  offer: Offer,
  _ctx: PdfRenderContext,
  def: OfferPdfTemplateDefinition,
  overrides?: SectionLayoutOverrides
): Promise<number> {
  if (!offer.notes) return y;
  const pageW = getPageWidth(def.page.format, def.page.orientation);
  const margin = overrides?.marginLeft ?? def.page.marginMm;
  const contentWidth = overrides?.contentWidth ?? pageW - 2 * def.page.marginMm;
  const t = def.tokens;
  const smallSize = t.smallFontSize ?? 8;
  doc.setFont(_ctx.font, 'normal');
  doc.setFontSize(smallSize);
  doc.setTextColor(100, 116, 139);
  const noteLines = doc.splitTextToSize(offer.notes, contentWidth);
  noteLines.forEach((line: string) => {
    doc.text(line, margin, y);
    y += 4;
  });
  return y + 6;
}

/** Draw totals breakdown (subtotal, discount, vat, total) at right side. Reusable across templates. */
function drawTotalsBreakdown(
  doc: import('jspdf').jsPDF,
  y: number,
  offer: Offer,
  font: string,
  LABELS: typeof LABELS_EN,
  margin: number,
  contentWidth: number,
  bodyFontSize: number,
  smallFontSize: number,
  primaryTextColor: [number, number, number],
  borderColor: [number, number, number],
  mutedTextColor: [number, number, number],
  useGreek: boolean,
): number {
  const rightX = margin + contentWidth;
  const labelX = rightX - 70;
  const valueX = rightX;
  const lineHeight = 5.5;

  const lineItems = offer.OfferLineItem ?? [];
  const grossAmount = lineItems.length > 0
    ? lineItems.reduce((sum, l) => sum + l.quantity * l.unitPrice, 0)
    : null;

  doc.setFont(font, 'normal');
  doc.setFontSize(smallFontSize);
  doc.setTextColor(...mutedTextColor);
  if (offer.currencyCode) {
    doc.text(useGreek ? `(Όλες οι τιμές σε ${offer.currencyCode})` : `(All amounts in ${offer.currencyCode})`, margin, y);
  } else {
    doc.text(LABELS.vatNote, margin, y);
  }
  y += 2;

  doc.setDrawColor(...borderColor);
  doc.setLineWidth(0.3);
  doc.line(margin, y, rightX, y);
  y += 6;

  doc.setFont(font, 'normal');
  doc.setFontSize(bodyFontSize);
  doc.setTextColor(...primaryTextColor);

  if (grossAmount != null) {
    doc.text(LABELS.grossAmount, labelX, y);
    doc.text(formatCurrency(grossAmount), valueX, y, { align: 'right' });
    y += lineHeight;
  }

  if (offer.discount != null && offer.discount > 0) {
    doc.text(LABELS.totalDiscount, labelX, y);
    doc.text(`-${formatCurrency(offer.discount)}`, valueX, y, { align: 'right' });
    y += lineHeight;
  }

  if (offer.subtotal != null) {
    doc.text(LABELS.netAmount, labelX, y);
    doc.text(formatCurrency(offer.subtotal), valueX, y, { align: 'right' });
    y += lineHeight;
  }

  const netAmount = offer.subtotal ?? 0;
  const vatAmount = offer.vatRate != null ? netAmount * (offer.vatRate / 100) : 0;

  if (offer.vatRate != null) {
    doc.text(`${LABELS.vatRate} ${Number(offer.vatRate)}%`, labelX, y);
    doc.text(formatCurrency(vatAmount), valueX, y, { align: 'right' });
    y += lineHeight;
  }

  doc.setDrawColor(...borderColor);
  doc.setLineWidth(0.2);
  doc.line(labelX, y, rightX, y);
  y += 4;

  const grandTotal = netAmount + vatAmount;
  doc.setFont(font, 'bold');
  doc.setFontSize(bodyFontSize + 1);
  doc.text(LABELS.grandTotal, labelX, y);
  doc.text(formatCurrency(grandTotal), valueX, y, { align: 'right' });
  y += 5;

  return y;
}

/** Draw total from definition. */
async function drawTotal(
  doc: import('jspdf').jsPDF,
  y: number,
  offer: Offer,
  ctx: PdfRenderContext,
  def: OfferPdfTemplateDefinition,
  overrides?: SectionLayoutOverrides
): Promise<number> {
  if (offer.total == null && offer.subtotal == null) return y;
  const pageW = getPageWidth(def.page.format, def.page.orientation);
  const margin = overrides?.marginLeft ?? def.page.marginMm;
  const contentWidth = overrides?.contentWidth ?? pageW - 2 * def.page.marginMm;
  const t = def.tokens;
  return drawTotalsBreakdown(
    doc, y, offer, ctx.font, ctx.LABELS,
    margin, contentWidth,
    t.bodyFontSize ?? 9, t.smallFontSize ?? 8,
    (t.primaryTextColor ?? [30, 41, 59]) as [number, number, number],
    (t.borderColor ?? [220, 223, 228]) as [number, number, number],
    [100, 116, 139],
    ctx.useGreek,
  );
}

/** Render PDF from a template definition (full designer path). */
async function renderFromDefinition(
  offer: Offer,
  ctx: PdfRenderContext,
  definition: OfferPdfTemplateDefinition,
  tableData: DefinitionTableData
): Promise<void> {
  const doc = ctx.doc;
  const def = definition;
  const margin = def.page.marginMm;
  const pageW = getPageWidth(def.page.format, def.page.orientation);
  ctx.contentWidth = pageW - 2 * margin;

  const sortedSections = [...def.sections].filter((s) => s.enabled).sort((a, b) => a.order - b.order);

  let y = 0;
  let headerStartY: number | null = null;
  let headerLeftEndY: number | null = null;
  let tableRendered = false;

  for (const section of sortedSections) {
    const pos = section.position;
    const useFixedY = !tableRendered && pos?.yMm != null;
    const startY = useFixedY ? pos!.yMm! : y;
    const overrides =
      pos != null && (pos.xMm != null || pos.widthMm != null || pos.heightMm != null)
        ? {
            marginLeft: pos.xMm,
            contentWidth: pos.widthMm,
            heightMm: pos.heightMm,
          }
        : undefined;

    const type = section.type as OfferPdfSectionType;
    if (type === 'accentBar') {
      y = await drawAccentBar(doc, startY, offer, ctx, def, overrides);
      continue;
    }
    if (type === 'letterhead') {
      headerStartY = startY;
      y = await drawLetterhead(doc, startY, offer, ctx, def, overrides);
      headerLeftEndY = y;
      continue;
    }
    if (type === 'titleMeta') {
      const metaStartY = headerStartY ?? startY;
      const metaEndY = await drawTitleMeta(doc, metaStartY, offer, ctx, def, overrides);
      if (headerLeftEndY != null) {
        y = Math.max(headerLeftEndY, metaEndY) + 10;
        headerStartY = null;
        headerLeftEndY = null;
      } else {
        y = metaEndY + 10;
      }
      continue;
    }
    if (type === 'intro') {
      y = await drawIntro(doc, startY, offer, ctx, def, overrides);
      continue;
    }
    if (type === 'lineItemsTable') {
      y = await drawLineItemsTable(doc, startY, offer, ctx, def, tableData, overrides);
      tableRendered = true;
      continue;
    }
    if (tableRendered) {
      y = ensureSpace(doc, y, 30, margin);
    }
    if (type === 'terms') {
      y = await drawTerms(doc, y, offer, ctx, def, overrides);
      continue;
    }
    if (type === 'notes') {
      y = await drawNotes(doc, y, offer, ctx, def, overrides);
      continue;
    }
    if (type === 'total') {
      y = await drawTotal(doc, y, offer, ctx, def, overrides);
      continue;
    }
  }
}

/** Classic layout: original design – accent bar, two-column header, meta box, terms box, table with product images. */
async function renderClassic(
  offer: Offer,
  ctx: PdfRenderContext,
  options: { MARGIN: number; rowImageData: (LoadedImageData | null)[]; rows: string[][]; tableHeaders: string[]; lineItems: OfferLineItem[] }
): Promise<void> {
  const { doc, font, LABELS, logoData, companyName, companyAddress, companyPhone, companyEmail, signatoryName, contentWidth } = ctx;
  const { MARGIN, rowImageData, rows, tableHeaders } = options;
  const autoTable = (await import('jspdf-autotable')).default;

  const ACCENT_COLOR: [number, number, number] = [41, 98, 255];
  const ACCENT_BAR_HEIGHT = 5;
  const META_BOX_COLOR: [number, number, number] = [245, 247, 250];
  const TERMS_BOX_COLOR: [number, number, number] = [248, 250, 252];
  const TABLE_HEADER_COLOR: [number, number, number] = [52, 73, 94];
  const BORDER_COLOR: [number, number, number] = [220, 223, 228];

  let y = 0;
  doc.setFillColor(...ACCENT_COLOR);
  doc.rect(0, 0, PAGE_WIDTH, ACCENT_BAR_HEIGHT, 'F');
  y = ACCENT_BAR_HEIGHT + 10;

  const colLeftWidth = contentWidth * 0.52;
  const colRightStart = MARGIN + colLeftWidth + 8;
  const metaBoxWidth = contentWidth - colLeftWidth - 8;
  const leftYStart = y;

  if (logoData && logoData.width > 0 && logoData.height > 0) {
    const scale = Math.min(LOGO_MAX_WIDTH_MM / (logoData.width / 3.78), LOGO_MAX_HEIGHT_MM / (logoData.height / 3.78), 1);
    const drawW = (logoData.width / 3.78) * scale;
    const drawH = (logoData.height / 3.78) * scale;
    const imgSrc = 'dataUrl' in logoData ? logoData.dataUrl : logoData.img;
    if (imgSrc) {
      doc.addImage(imgSrc, logoData.format, MARGIN, y, drawW, drawH);
      y += drawH + 5;
    }
  }

  doc.setTextColor(51, 65, 85);
  if (companyName) {
    doc.setFont(font, 'bold');
    doc.setFontSize(11);
    doc.text(companyName, MARGIN, y);
    y += 6;
  }
  if (companyAddress) {
    doc.setFont(font, 'normal');
    doc.setFontSize(9);
    const addrLines = doc.splitTextToSize(companyAddress, colLeftWidth);
    addrLines.forEach((line: string) => {
      doc.text(line, MARGIN, y);
      y += 4.5;
    });
  }
  if (companyPhone || companyEmail) {
    doc.setFontSize(9);
    if (companyPhone) {
      doc.text(`Tel: ${companyPhone}`, MARGIN, y);
      y += 4.5;
    }
    if (companyEmail) {
      doc.text(`Email: ${companyEmail}`, MARGIN, y);
      y += 4.5;
    }
  }
  if (signatoryName) {
    doc.setFont(font, 'bold');
    doc.setFontSize(9);
    doc.text(signatoryName, MARGIN, y);
    y += 5;
  }

  const leftBlockEndY = y;

  doc.setFont(font, 'bold');
  doc.setFontSize(20);
  doc.setTextColor(30, 41, 59);
  doc.text(LABELS.title, colRightStart + 4, leftYStart + 5);

  const metaBoxY = leftYStart + 10;
  const metaLines: string[] = [];
  metaLines.push(`${LABELS.offerNo} ${offer.offerNumber}`);
  metaLines.push(`${LABELS.to} ${ctx.clientName}`);
  if (offer.signatoryName) metaLines.push(`${LABELS.signatory} ${offer.signatoryName}`);
  metaLines.push(`${LABELS.date} ${formatDate(offer.offerDate)}`);
  if (offer.validUntil) metaLines.push(`${LABELS.validUntil} ${formatDate(offer.validUntil)}`);
  const metaBoxHeight = Math.max(32, metaLines.length * 6 + 8);
  doc.setDrawColor(...BORDER_COLOR);
  doc.setLineWidth(0.2);
  doc.setFillColor(...META_BOX_COLOR);
  doc.roundedRect(colRightStart, metaBoxY, metaBoxWidth, metaBoxHeight, 1.5, 1.5, 'FD');

  doc.setFont(font, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(51, 65, 85);
  metaLines.forEach((line, i) => {
    doc.text(line, colRightStart + 4, metaBoxY + 8 + i * 6);
  });

  y = Math.max(leftBlockEndY, metaBoxY + metaBoxHeight) + 10;

  if (offer.introText) {
    doc.setFont(font, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    const introLines = doc.splitTextToSize(offer.introText, contentWidth);
    introLines.forEach((line: string) => {
      doc.text(line, MARGIN, y);
      y += 5;
    });
    y += 6;
  }

  if (rows.length > 0) {
    autoTable(doc, {
      head: [tableHeaders],
      body: rows,
      startY: y,
      styles: { fontSize: 9, cellPadding: 3, font: font, textColor: [51, 65, 85] },
      headStyles: {
        fillColor: TABLE_HEADER_COLOR,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        font: font,
        fontSize: 9,
        cellPadding: 4,
      },
      alternateRowStyles: { fillColor: [250, 250, 252] },
      margin: { top: MARGIN, bottom: MARGIN, left: MARGIN, right: MARGIN },
      columnStyles: rows[0]?.length === 6
        ? {
            0: { cellWidth: ROW_IMAGE_SIZE_MM + 4, minCellHeight: ROW_IMAGE_SIZE_MM + 4 },
            1: { cellWidth: 'auto' },
            2: { halign: 'right', valign: 'middle', cellWidth: 14 },
            3: { halign: 'right', valign: 'middle', cellWidth: 24 },
            4: { halign: 'right', valign: 'middle', cellWidth: 22 },
            5: { halign: 'right', valign: 'middle', cellWidth: 24 },
          }
        : {
            0: { cellWidth: ROW_IMAGE_SIZE_MM + 4, minCellHeight: ROW_IMAGE_SIZE_MM + 4 },
            1: { cellWidth: 'auto' },
            2: { halign: 'right', valign: 'middle', cellWidth: 16 },
            3: { halign: 'right', valign: 'middle', cellWidth: 28 },
            4: { halign: 'right', valign: 'middle', cellWidth: 28 },
          },
      theme: 'plain',
      rowPageBreak: 'avoid',
      didDrawCell: (data) => {
        if (data.section !== 'body') return;
        if (data.column.index === 0) {
          const dataItem = rowImageData[data.row.index];
          if (dataItem?.dataUrl && dataItem.width > 0 && dataItem.height > 0) {
            const cell = data.cell;
            const pad = 1.5;
            const w = cell.width - pad * 2;
            const h = cell.height - pad * 2;
            const scale = Math.min(w / (dataItem.width / 3.78), h / (dataItem.height / 3.78), 1);
            const drawW = (dataItem.width / 3.78) * scale;
            const drawH = (dataItem.height / 3.78) * scale;
            const cx = cell.x + cell.width / 2;
            const cy = cell.y + cell.height / 2;
            doc.addImage(dataItem.dataUrl, dataItem.format, cx - drawW / 2, cy - drawH / 2, drawW, drawH);
          }
        }
        if (data.column.index === 1) {
          const li = options.lineItems[data.row.index];
          if (li) drawProductCell(doc, data.cell, li, font, 9, [51, 65, 85]);
        }
      },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  }

  if (offer.total != null || offer.subtotal != null) {
    y = ensureSpace(doc, y, 40, MARGIN);
    y = drawTotalsBreakdown(
      doc, y, offer, font, LABELS,
      MARGIN, contentWidth, 9, 8,
      [30, 41, 59], BORDER_COLOR, [100, 116, 139],
      ctx.useGreek,
    );
    y += 6;
  }

  const hasTerms = !!(offer.validityText || offer.deliveryText || offer.paymentMethodText);
  if (hasTerms) {
    const termLines: string[] = [];
    if (offer.validityText) termLines.push(`${LABELS.validity} ${offer.validityText}`);
    if (offer.deliveryText) termLines.push(`${LABELS.delivery} ${offer.deliveryText}`);
    if (offer.paymentMethodText) termLines.push(`${LABELS.payment} ${offer.paymentMethodText}`);
    const termBoxPadding = 5;
    const termBoxHeight = termLines.length * 5 + termBoxPadding * 2;
    y = ensureSpace(doc, y, termBoxHeight + 6, MARGIN);
    doc.setFillColor(...TERMS_BOX_COLOR);
    doc.setDrawColor(...BORDER_COLOR);
    doc.roundedRect(MARGIN, y - 2, contentWidth, termBoxHeight, 1.5, 1.5, 'FD');
    doc.setFontSize(9);
    termLines.forEach((line: string, i: number) => {
      doc.text(line, MARGIN + termBoxPadding, y + 5 + i * 5);
    });
    y += termBoxHeight + 6;
  }

  if (offer.notes) {
    y = ensureSpace(doc, y, 20, MARGIN);
    doc.setFont(font, 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    const noteLines = doc.splitTextToSize(offer.notes, contentWidth);
    noteLines.forEach((line: string) => {
      y = ensureSpace(doc, y, 6, MARGIN);
      doc.text(line, MARGIN, y);
      y += 4;
    });
    y += 6;
  }
}

/** Minimal layout: clean typography, no colored boxes, underline dividers, text-only table (no product images). */
async function renderMinimal(offer: Offer, ctx: PdfRenderContext, options: { MARGIN: number; rowsTextOnly: string[][]; tableHeadersMinimal: string[]; lineItems: OfferLineItem[] }): Promise<void> {
  const { doc, font, LABELS, logoData, companyName, companyAddress, companyPhone, companyEmail, signatoryName, contentWidth } = ctx;
  const { MARGIN, rowsTextOnly, tableHeadersMinimal } = options;
  const autoTable = (await import('jspdf-autotable')).default;

  const TEXT_DARK = [30, 41, 59] as [number, number, number];
  const TEXT_MUTED = [71, 85, 105] as [number, number, number];
  const LINE_COLOR = [226, 232, 240] as [number, number, number];

  let y = MARGIN;

  if (logoData && logoData.width > 0 && logoData.height > 0) {
    const scale = Math.min(LOGO_MAX_WIDTH_MM / (logoData.width / 3.78), LOGO_MAX_HEIGHT_MM / (logoData.height / 3.78), 1);
    const drawW = (logoData.width / 3.78) * scale;
    const drawH = (logoData.height / 3.78) * scale;
    const imgSrc = 'dataUrl' in logoData ? logoData.dataUrl : logoData.img;
    if (imgSrc) {
      doc.addImage(imgSrc, logoData.format, MARGIN, y, drawW, drawH);
      y += drawH + 8;
    }
  }

  doc.setTextColor(...TEXT_DARK);
  if (companyName) {
    doc.setFont(font, 'bold');
    doc.setFontSize(10);
    doc.text(companyName, MARGIN, y);
    y += 5;
  }
  if (companyAddress) {
    doc.setFont(font, 'normal');
    doc.setFontSize(9);
    const addrLines = doc.splitTextToSize(companyAddress, contentWidth * 0.5);
    addrLines.forEach((line: string) => {
      doc.text(line, MARGIN, y);
      y += 4;
    });
  }
  if (companyPhone || companyEmail) {
    doc.setFontSize(9);
    if (companyPhone) {
      doc.text(`Tel: ${companyPhone}`, MARGIN, y);
      y += 4;
    }
    if (companyEmail) {
      doc.text(companyEmail, MARGIN, y);
      y += 4;
    }
  }
  if (signatoryName) {
    doc.setFont(font, 'bold');
    doc.setFontSize(9);
    doc.text(signatoryName, MARGIN, y);
    y += 6;
  }

  y += 4;
  doc.setDrawColor(...LINE_COLOR);
  doc.setLineWidth(0.2);
  doc.line(MARGIN, y, MARGIN + contentWidth, y);
  y += 12;

  doc.setFont(font, 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...TEXT_DARK);
  doc.text(LABELS.title, MARGIN, y);
  y += 8;

  doc.setFont(font, 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...TEXT_MUTED);
  let metaLine1 = `${LABELS.offerNo} ${offer.offerNumber}  ·  ${LABELS.date} ${formatDate(offer.offerDate)}`;
  if (offer.validUntil) metaLine1 += `  ·  ${LABELS.validUntil} ${formatDate(offer.validUntil)}`;
  doc.text(metaLine1, MARGIN, y);
  y += 5;
  doc.text(`${LABELS.to} ${ctx.clientName}`, MARGIN, y);
  y += 5;
  if (offer.signatoryName) {
    doc.text(`${LABELS.signatory} ${offer.signatoryName}`, MARGIN, y);
    y += 5;
  }
  y += 5;

  if (offer.introText) {
    doc.setFontSize(9);
    const introLines = doc.splitTextToSize(offer.introText, contentWidth);
    introLines.forEach((line: string) => {
      doc.text(line, MARGIN, y);
      y += 5;
    });
    y += 6;
  }

  if (rowsTextOnly.length > 0) {
    autoTable(doc, {
      head: [tableHeadersMinimal],
      body: rowsTextOnly,
      startY: y,
      styles: { fontSize: 9, cellPadding: 4, font: font, textColor: TEXT_DARK },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: TEXT_MUTED,
        fontStyle: 'bold',
        font: font,
        fontSize: 8,
        cellPadding: 4,
        lineColor: LINE_COLOR,
        lineWidth: 0.2,
      },
      margin: { top: MARGIN, bottom: MARGIN, left: MARGIN, right: MARGIN },
      columnStyles: rowsTextOnly[0]?.length === 5
        ? {
            0: { cellWidth: 'auto' },
            1: { halign: 'right', valign: 'middle', cellWidth: 14 },
            2: { halign: 'right', valign: 'middle', cellWidth: 24 },
            3: { halign: 'right', valign: 'middle', cellWidth: 22 },
            4: { halign: 'right', valign: 'middle', cellWidth: 24 },
          }
        : {
            0: { cellWidth: 'auto' },
            1: { halign: 'right', valign: 'middle', cellWidth: 14 },
            2: { halign: 'right', valign: 'middle', cellWidth: 24 },
            3: { halign: 'right', valign: 'middle', cellWidth: 24 },
          },
      theme: 'plain',
      rowPageBreak: 'avoid',
      tableLineColor: LINE_COLOR,
      tableLineWidth: 0.2,
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 0) {
          const li = options.lineItems[data.row.index];
          if (li) drawProductCell(doc, data.cell, li, font, 9, TEXT_DARK);
        }
      },
    });
    y = (doc as any).lastAutoTable.finalY + 10;
  }

  if (offer.total != null || offer.subtotal != null) {
    y = ensureSpace(doc, y, 40, MARGIN);
    y = drawTotalsBreakdown(
      doc, y, offer, font, LABELS,
      MARGIN, contentWidth, 9, 8,
      TEXT_DARK, LINE_COLOR, TEXT_MUTED,
      ctx.useGreek,
    );
    y += 6;
  }

  if (offer.validityText || offer.deliveryText || offer.paymentMethodText) {
    y = ensureSpace(doc, y, 16, MARGIN);
    doc.setDrawColor(...LINE_COLOR);
    doc.setLineWidth(0.2);
    doc.line(MARGIN, y, MARGIN + contentWidth, y);
    y += 6;
    doc.setFont(font, 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...TEXT_MUTED);
    const parts: string[] = [];
    if (offer.validityText) parts.push(`${LABELS.validity} ${offer.validityText}`);
    if (offer.deliveryText) parts.push(`${LABELS.delivery} ${offer.deliveryText}`);
    if (offer.paymentMethodText) parts.push(`${LABELS.payment} ${offer.paymentMethodText}`);
    doc.text(parts.join('  ·  '), MARGIN, y);
    y += 8;
  }

  if (offer.notes) {
    y = ensureSpace(doc, y, 20, MARGIN);
    doc.setFontSize(8);
    const noteLines = doc.splitTextToSize(offer.notes, contentWidth);
    noteLines.forEach((line: string) => {
      y = ensureSpace(doc, y, 6, MARGIN);
      doc.text(line, MARGIN, y);
      y += 4;
    });
    y += 6;
  }
}

/** Compact layout: invoice-style one-line header (logo | offer# · date · client), dense content, total in a highlighted strip. */
async function renderCompact(
  offer: Offer,
  ctx: PdfRenderContext,
  options: { MARGIN: number; rowImageData: (LoadedImageData | null)[]; rows: string[][]; tableHeaders: string[]; lineItems: OfferLineItem[] }
): Promise<void> {
  const { doc, font, LABELS, logoData, companyName, companyAddress, companyPhone, companyEmail, contentWidth } = ctx;
  const { MARGIN, rowImageData, rows, tableHeaders } = options;
  const autoTable = (await import('jspdf-autotable')).default;

  const ACCENT = [41, 98, 255] as [number, number, number];
  const TEXT_DARK = [30, 41, 59] as [number, number, number];
  const TEXT_MUTED = [71, 85, 105] as [number, number, number];
  const BORDER = [218, 222, 226] as [number, number, number];

  let y = MARGIN;

  const headerH = 14;
  doc.setFillColor(...ACCENT);
  doc.rect(0, 0, PAGE_WIDTH, headerH, 'F');

  let logoDrawW = 0;
  let logoDrawH = 0;
  if (logoData && logoData.width > 0 && logoData.height > 0) {
    const scale = Math.min(35 / (logoData.width / 3.78), 10 / (logoData.height / 3.78), 1);
    logoDrawW = (logoData.width / 3.78) * scale;
    logoDrawH = (logoData.height / 3.78) * scale;
    const imgSrc = 'dataUrl' in logoData ? logoData.dataUrl : logoData.img;
    if (imgSrc) {
      doc.addImage(imgSrc, logoData.format, MARGIN, (headerH - logoDrawH) / 2, logoDrawW, logoDrawH);
    }
  }

  doc.setFont(font, 'bold');
  doc.setFontSize(10);
  doc.setTextColor(255, 255, 255);
  const metaParts = [`${offer.offerNumber}`, formatDate(offer.offerDate), `${LABELS.to} ${ctx.clientName}`];
  if (offer.signatoryName) metaParts.push(`${LABELS.signatory} ${offer.signatoryName}`);
  if (offer.validUntil) metaParts.push(`${LABELS.validUntil} ${formatDate(offer.validUntil)}`);
  doc.text(metaParts.join('  ·  '), MARGIN + logoDrawW + 6, headerH / 2 + 1.5);

  y = headerH + 10;

  doc.setTextColor(...TEXT_DARK);
  if (companyName || companyAddress || companyPhone || companyEmail) {
    doc.setFont(font, 'normal');
    doc.setFontSize(8);
    if (companyName) {
      doc.text(companyName, MARGIN, y);
      y += 4;
    }
    if (companyAddress) {
      const addrLines = doc.splitTextToSize(companyAddress, contentWidth * 0.45);
      addrLines.forEach((line: string) => {
        doc.text(line, MARGIN, y);
        y += 3.5;
      });
    }
    if (companyPhone || companyEmail) {
      const contact = [companyPhone && `Tel: ${companyPhone}`, companyEmail].filter(Boolean).join('  ·  ');
      doc.text(contact, MARGIN, y);
      y += 4;
    }
    y += 4;
  }

  doc.setFont(font, 'bold');
  doc.setFontSize(14);
  doc.text(LABELS.title, MARGIN, y);
  y += 6;

  if (offer.introText) {
    doc.setFont(font, 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...TEXT_MUTED);
    const introLines = doc.splitTextToSize(offer.introText, contentWidth);
    introLines.forEach((line: string) => {
      doc.text(line, MARGIN, y);
      y += 4;
    });
    y += 4;
  }

  if (rows.length > 0) {
    autoTable(doc, {
      head: [tableHeaders],
      body: rows,
      startY: y,
      styles: { fontSize: 8, cellPadding: 2.5, font: font, textColor: TEXT_DARK },
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: TEXT_DARK,
        fontStyle: 'bold',
        font: font,
        fontSize: 8,
        cellPadding: 2.5,
        lineColor: BORDER,
        lineWidth: 0.15,
      },
      margin: { top: MARGIN, bottom: MARGIN, left: MARGIN, right: MARGIN },
      columnStyles: rows[0]?.length === 6
        ? {
            0: { cellWidth: 14, minCellHeight: 12 },
            1: { cellWidth: 'auto' },
            2: { halign: 'right', valign: 'middle', cellWidth: 12 },
            3: { halign: 'right', valign: 'middle', cellWidth: 20 },
            4: { halign: 'right', valign: 'middle', cellWidth: 18 },
            5: { halign: 'right', valign: 'middle', cellWidth: 20 },
          }
        : {
            0: { cellWidth: 14, minCellHeight: 12 },
            1: { cellWidth: 'auto' },
            2: { halign: 'right', valign: 'middle', cellWidth: 12 },
            3: { halign: 'right', valign: 'middle', cellWidth: 22 },
            4: { halign: 'right', valign: 'middle', cellWidth: 22 },
          },
      theme: 'plain',
      rowPageBreak: 'avoid',
      tableLineColor: BORDER,
      tableLineWidth: 0.15,
      didDrawCell: (data) => {
        if (data.section !== 'body') return;
        if (data.column.index === 0) {
          const dataItem = rowImageData[data.row.index];
          if (dataItem?.dataUrl && dataItem.width > 0 && dataItem.height > 0) {
            const cell = data.cell;
            const pad = 1;
            const w = cell.width - pad * 2;
            const h = cell.height - pad * 2;
            const scale = Math.min(w / (dataItem.width / 3.78), h / (dataItem.height / 3.78), 1);
            const drawW = (dataItem.width / 3.78) * scale;
            const drawH = (dataItem.height / 3.78) * scale;
            const cx = cell.x + cell.width / 2;
            const cy = cell.y + cell.height / 2;
            doc.addImage(dataItem.dataUrl, dataItem.format, cx - drawW / 2, cy - drawH / 2, drawW, drawH);
          }
        }
        if (data.column.index === 1) {
          const li = options.lineItems[data.row.index];
          if (li) drawProductCell(doc, data.cell, li, font, 8, TEXT_DARK);
        }
      },
    });
    y = (doc as any).lastAutoTable.finalY + 6;
  }

  if (offer.total != null || offer.subtotal != null) {
    y = ensureSpace(doc, y, 35, MARGIN);
    y = drawTotalsBreakdown(
      doc, y, offer, font, LABELS,
      MARGIN, contentWidth, 8, 7,
      TEXT_DARK, BORDER, TEXT_MUTED,
      ctx.useGreek,
    );
    y += 4;
  }

  const footerParts: string[] = [];
  if (offer.validityText) footerParts.push(`${LABELS.validity} ${offer.validityText}`);
  if (offer.deliveryText) footerParts.push(`${LABELS.delivery} ${offer.deliveryText}`);
  if (offer.paymentMethodText) footerParts.push(`${LABELS.payment} ${offer.paymentMethodText}`);
  if (offer.notes) footerParts.push(offer.notes);
  if (footerParts.length > 0) {
    y = ensureSpace(doc, y, 15, MARGIN);
    doc.setFont(font, 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...TEXT_MUTED);
    const footerText = footerParts.join('  |  ');
    const footerLines = doc.splitTextToSize(footerText, contentWidth);
    footerLines.forEach((line: string) => {
      y = ensureSpace(doc, y, 5, MARGIN);
      doc.text(line, MARGIN, y);
      y += 3.5;
    });
  }
}

export async function exportOfferToPdf(offer: Offer, options?: OfferPdfExportOptions): Promise<void | Blob> {
  const jsPDF = (await import('jspdf')).default;
  const templateDefinition = options?.templateDefinition ?? null;
  const templateId: OfferPdfTemplateId = options?.templateId ?? 'classic';

  const useDefinition = templateDefinition != null && templateDefinition.page != null && Array.isArray(templateDefinition.sections) && templateDefinition.tokens != null;

  const pageFormat = useDefinition ? templateDefinition.page.format : 'a4';
  const orientation = useDefinition ? templateDefinition.page.orientation : 'portrait';

  const doc = new jsPDF({ orientation, unit: 'mm', format: pageFormat });

  const greekBase64 = await fetchGreekFontBase64();
  const useGreek = greekBase64 && registerGreekFont(doc, greekBase64);
  const LABELS = useGreek ? LABELS_EL : LABELS_EN;
  const font = useGreek ? GREEK_FONT_ID : 'helvetica';

  const logoUrl = options?.logoUrlOverride?.trim();
  let logoData: LoadedImageData | { img: HTMLImageElement; format: 'PNG' | 'JPEG'; width: number; height: number } | null = null;
  if (logoUrl) {
    if (options?.logoRequiresAuth) {
      const data = await loadImageViaApi(logoUrl);
      if (data) logoData = data;
    } else {
      const img = await loadLogoImage(logoUrl);
      if (img && img.width > 0 && img.height > 0) {
        logoData = { img, format: /jpe?g|image\/jpeg/i.test(logoUrl) ? 'JPEG' : 'PNG', width: img.width, height: img.height };
      }
    }
  }

  const companyName = options?.companyDetails?.companyName ?? offer.companyName ?? null;
  const companyAddress = options?.companyDetails?.companyAddress ?? offer.companyAddress ?? null;
  const companyPhone = options?.companyDetails?.companyPhone ?? offer.companyPhone ?? null;
  const companyEmail = options?.companyDetails?.companyEmail ?? offer.companyEmail ?? null;
  const signatoryName = options?.companyDetails?.signatoryName ?? offer.signatoryName ?? null;
  const clientName = offer.client?.clientName || offer.client?.clientName2 || offer.lead?.leadDescr || '—';

  const lineItems = offer.OfferLineItem || [];
  const hasLineDiscounts = lineItems.some((l) => (l.discount ?? 0) > 0);
  const productLabel = useGreek ? 'Προϊόν' : 'Product';
  const tableHeadersClassic = hasLineDiscounts
    ? ['', productLabel, LABELS.qty, LABELS.unitPrice, LABELS.discount, LABELS.total]
    : ['', productLabel, LABELS.qty, LABELS.unitPrice, LABELS.total];
  const rowImageUrls = lineItems.map((l) => getCatalogItemImageUrl(l));
  const rowImageData: (LoadedImageData | null)[] = await Promise.all(
    rowImageUrls.map((url) => (url ? loadImageViaApi(url) : Promise.resolve(null)))
  );
  const productCell = (l: OfferLineItem) => [l.name, l.description].filter(Boolean).join('\n') || '—';
  const rowsClassic = lineItems.map((l) => hasLineDiscounts
    ? ['', productCell(l), String(l.quantity), formatCurrency(l.unitPrice), formatCurrency(l.discount ?? 0), formatCurrency(l.amount)]
    : ['', productCell(l), String(l.quantity), formatCurrency(l.unitPrice), formatCurrency(l.amount)]
  );

  const tableHeadersMinimal = hasLineDiscounts
    ? [productLabel, LABELS.qty, LABELS.unitPrice, LABELS.discount, LABELS.total]
    : [productLabel, LABELS.qty, LABELS.unitPrice, LABELS.total];
  const rowsTextOnly = lineItems.map((l) => {
    return hasLineDiscounts
      ? [productCell(l), String(l.quantity), formatCurrency(l.unitPrice), formatCurrency(l.discount ?? 0), formatCurrency(l.amount)]
      : [productCell(l), String(l.quantity), formatCurrency(l.unitPrice), formatCurrency(l.amount)];
  });

  if (useDefinition) {
    const def = templateDefinition as OfferPdfTemplateDefinition;
    const pageW = getPageWidth(def.page.format, def.page.orientation);
    const ctx: PdfRenderContext = {
      doc,
      font,
      LABELS,
      useGreek: !!useGreek,
      logoData,
      companyName,
      companyAddress,
      companyPhone,
      companyEmail,
      signatoryName,
      contentWidth: pageW - 2 * def.page.marginMm,
      clientName,
    };
    const tableData: DefinitionTableData = {
      rowImageData,
      rowsClassic,
      rowsTextOnly,
      tableHeadersClassic,
      tableHeadersMinimal,
      lineItems,
    };
    await renderFromDefinition(offer, ctx, def, tableData);
  } else {
    const MARGIN = templateId === 'compact' ? 12 : 16;
    const contentWidth = PAGE_WIDTH - 2 * MARGIN;
    const ctx: PdfRenderContext = {
      doc,
      font,
      LABELS,
      useGreek: !!useGreek,
      logoData,
      companyName,
      companyAddress,
      companyPhone,
      companyEmail,
      signatoryName,
      contentWidth,
      clientName,
    };
    if (templateId === 'classic') {
      await renderClassic(offer, ctx, { MARGIN: 16, rowImageData, rows: rowsClassic, tableHeaders: tableHeadersClassic, lineItems });
    } else if (templateId === 'minimal') {
      await renderMinimal(offer, ctx, { MARGIN: 16, rowsTextOnly, tableHeadersMinimal, lineItems });
    } else {
      await renderCompact(offer, ctx, { MARGIN, rowImageData, rows: rowsClassic, tableHeaders: tableHeadersClassic, lineItems });
    }
  }

  doc.setTextColor(0, 0, 0);
  if (options?.returnBlob) {
    const blob = doc.output('blob');
    return Promise.resolve(blob);
  }
  const safeNumber = (offer.offerNumber || String(offer.id)).replace(/\//g, '-').replace(/\s/g, '_');
  doc.save(`offer_${safeNumber}.pdf`);
}
